import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { BomView } from "@/components/BomView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Inbox, Phone, Mail, MapPin, Building2, User, FileText, Trash2 } from "lucide-react";

const STATUSES = [
  { key: "", label: "Toate" },
  { key: "nou", label: "Noi" },
  { key: "in_lucru", label: "În lucru" },
  { key: "ofertat", label: "Ofertate" },
  { key: "inchis", label: "Închise" },
];
const STATUS_CLS = {
  nou: "bg-primary/20 text-primary",
  in_lucru: "bg-blue-500/20 text-blue-300",
  ofertat: "bg-amber-500/20 text-amber-300",
  inchis: "bg-slate-700 text-slate-300",
};
const fmt = (n) => new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2 }).format(n || 0);

export default function Sales() {
  const { user: me } = useAuth();
  const { t, lang } = useT();
  const [offers, setOffers] = useState(null);
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ status: "", sales_notes: "", offer_total: "" });

  const load = useCallback(async () => {
    setOffers(null);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/offers", { params });
      setOffers(data.offers);
    } catch (e) { toast.error(apiErr(e)); setOffers([]); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openOffer = (o) => {
    setSel(o);
    setForm({ status: o.status, sales_notes: o.sales_notes || "", offer_total: o.offer_total ?? "" });
  };

  const save = async () => {
    try {
      const payload = {
        status: form.status,
        sales_notes: form.sales_notes,
        offer_total: form.offer_total === "" ? null : parseFloat(form.offer_total),
      };
      const { data } = await api.patch(`/offers/${sel.id}`, payload);
      toast.success(t("Cerere actualizată"));
      setSel(data);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm(t("Ștergi definitiv această cerere de ofertă?"))) return;
    try {
      await api.delete(`/admin/offers/${id}`);
      toast.success(t("Cerere ștearsă"));
      setSel(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/offers/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oferta-klus-${String(id).slice(0, 8)}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <AppLayout>
      <h1 className="text-3xl font-extrabold text-white mb-1">{t("Cereri de ofertă")}</h1>
      <p className="text-slate-400 text-sm mb-6">{t("Gestionează solicitările clienților și completează ofertele.")}</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`offer-filter-${s.key || "all"}`}
            className={`px-4 py-2 rounded-full text-sm border transition-all ${filter === s.key ? "border-primary bg-primary/15 text-white" : "border-slate-700 text-slate-400 hover:border-slate-600"}`}>
            {t(s.label)}
          </button>
        ))}
      </div>

      {offers === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : offers.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Inbox className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p>{t("Nicio cerere de ofertă.")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {offers.map((o) => (
            <div key={o.id} onClick={() => openOffer(o)} data-testid={`offer-card-${o.id}`}
              className="rounded-2xl border border-slate-800 bg-card hover:border-primary/40 transition-colors cursor-pointer p-5">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <h3 className="font-display font-semibold text-white">{o.user_name || o.user_email}</h3>
                  <p className="text-xs text-slate-500">{o.project_name} · {new Date(o.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "ro-RO")}</p>
                </div>
                <Badge className={`border-0 ${STATUS_CLS[o.status] || ""}`}>{t(o.status)}</Badge>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {o.user_email}</span>
                {o.user_phone?.trim() && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {o.user_phone}</span>}
                {o.user_county && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.user_county}</span>}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800">
                <span className="text-xs text-slate-500">{t("Estimare:")} <b className="text-slate-300 font-mono">{fmt(o.bom_snapshot?.estimated_total)} lei</b></span>
                {o.offer_total != null && <span className="text-xs text-accent font-mono">{t("Ofertă:")} {fmt(o.offer_total)} lei</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-card border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{t("Cerere ofertă")}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-3 rounded-xl bg-slate-900/60 border border-slate-800 p-4 text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  {sel.client_type === "juridica" ? <Building2 className="w-4 h-4 text-primary" /> : <User className="w-4 h-4 text-primary" />}
                  {sel.user_name} {sel.company_name && `· ${sel.company_name}`}
                </div>
                <div className="flex items-center gap-2 text-slate-300"><Mail className="w-4 h-4 text-slate-500" /> {sel.user_email}</div>
                <div className="flex items-center gap-2 text-slate-300"><Phone className="w-4 h-4 text-slate-500" /> {sel.user_phone || "—"}</div>
                <div className="flex items-center gap-2 text-slate-300"><MapPin className="w-4 h-4 text-slate-500" /> {sel.user_county || "—"}</div>
              </div>

              {sel.message && (
                <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">{t("Mesaj client")}</p>
                  <p className="text-sm text-slate-300">{sel.message}</p>
                </div>
              )}
              {sel.style_key && <p className="text-xs text-slate-400">{t("Variantă aleasă:")} <b className="text-slate-200">{sel.style_key}</b></p>}

              <Button variant="outline" onClick={() => downloadPdf(sel.id)} data-testid="offer-pdf-btn"
                className="border-slate-700 text-slate-200 hover:bg-slate-800 w-full">
                <FileText className="w-4 h-4 mr-1.5" /> {t("Descarcă oferta PDF")}
              </Button>

              <BomView bom={sel.bom_snapshot} />

              <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">{t("Status")}</label>
                  <Select value={form.status} onValueChange={(v) => setForm((s) => ({ ...s, status: v }))}>
                    <SelectTrigger data-testid="offer-status-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["nou", "in_lucru", "ofertat", "inchis"].map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">{t("Total ofertă (lei)")}</label>
                  <Input type="number" value={form.offer_total} onChange={(e) => setForm((s) => ({ ...s, offer_total: e.target.value }))}
                    data-testid="offer-total-input" className="mt-1.5 bg-slate-900 border-slate-700" placeholder="0.00" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-slate-400">{t("Note interne")}</label>
                  <Textarea value={form.sales_notes} onChange={(e) => setForm((s) => ({ ...s, sales_notes: e.target.value }))}
                    data-testid="offer-notes" className="mt-1.5 bg-slate-900 border-slate-700" />
                </div>
              </div>
              <Button onClick={save} data-testid="offer-save" className="w-full bg-primary hover:bg-primary/90 text-white">{t("Salvează")}</Button>
              {me?.role === "admin" && (
                <Button onClick={() => deleteOffer(sel.id)} data-testid="offer-delete-btn" variant="outline"
                  className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4 mr-1.5" /> {t("Șterge cererea")}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
